import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, TableSkeleton,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton,
  CustomSelect, StatusDot, Amount, btnPrimary, btnSecondary, btnDisabled,
} from '@redpanda/ui';
import {
  useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, isAdmin, formatTableDate,
  useRefetchOnFocus, useRememberedState,
} from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import JournalDialog from '../components/JournalDialog';
import { JournalEntry, useJournalStoreBase } from '../stores/journalStore';
import { useAccountOptionsStore } from '../stores/accountStore';
import { bulkDeleteJournalEntries, getJournalEntries, setJournalPosted, setJournalsPosted } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { apiErrorMessage } from '../utils/apiError';

const COLUMNS = 6;
type StatusFilter = 'ALL' | 'DRAFT' | 'POSTED';
const STATUS_FILTERS: readonly StatusFilter[] = ['ALL', 'DRAFT', 'POSTED'];

/**
 * 記帳分錄：草稿 → 過帳（過帳後才計入科目餘額與報表）
 * 已過帳的不能改、不能刪，要先取消過帳；一般使用者只能改、刪自己建立的
 */
const JournalListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;
  const admin = isAdmin(userRole);
  const canModify = (journal: JournalEntry) => admin || journal.userId === user?.id;

  const store = useJournalStoreBase();
  const { items: journals, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;
  const accounts = useAccountOptionsStore(state => state.accounts);
  const accountsLoaded = useAccountOptionsStore(state => state.loaded);
  const fetchAccounts = useAccountOptionsStore(state => state.fetchAll);
  const noAccounts = accountsLoaded && accounts.length === 0;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useRememberedState<StatusFilter>('accounting-ui:journals.status', 'ALL', STATUS_FILTERS);
  const [dialogJournal, setDialogJournal] = useState<JournalEntry | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [viewOnly, setViewOnly] = useState(false);
  const openDialog = (journal: JournalEntry | null, readOnly = false) => {
    setViewOnly(readOnly);
    setDialogJournal(journal);
  };
  const [pendingPost, setPendingPost] = useState<{ ids: string[]; posted: boolean } | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [postingId, setPostingId] = useState<string | null>(null);

  useEffect(() => {
    fetchItems(1);
    fetchAccounts();
  }, [fetchItems, fetchAccounts]);
  useRefetchOnFocus(() => useJournalStoreBase.getState().refreshData());

  // 搜尋、狀態篩選由後端查全部分錄（R29）
  const search = searchTerm.trim();
  const status = statusFilter === 'ALL' ? undefined : statusFilter;
  const hasFilters = !!search || !!status;
  const searchResult = useListSearch<JournalEntry>('journals', hasFilters ? { search, status } : null, page => getJournalEntries(page, 20, { search, status }));
  const listItems = searchResult.active ? searchResult.items : journals;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useJournalStoreBase.getState(), fetchItems, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('journals.item'),
    bulkDeleteApi: bulkDeleteJournalEntries,
    canDeleteItem: (journal: JournalEntry) => !journal.source,
    userRole,
    t,
    toast,
    pagination: listPagination ? { currentPage: listPagination.currentPage, totalPages: listPagination.totalPages } : undefined,
    fetchItems,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(useJournalStoreBase.setState, 'journal', () => journals, () => pagination),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('journals');
    },
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  // 發票自動產生的分錄不能批次過帳、取消過帳
  const selected = listItems.filter(journal => bulkActions.selectedItems.includes(journal.id) && !journal.source);
  const draftIds = selected.filter(j => !j.isPosted).map(j => j.id);
  const postedIds = selected.filter(j => j.isPosted).map(j => j.id);

  /** 過帳／取消過帳：等伺服器回應（餘額要由後端重算），成功後更新列表與報表 */
  const applyPosted = async (ids: string[], posted: boolean) => {
    setIsPosting(true);
    if (ids.length === 1) setPostingId(ids[0]);
    try {
      if (ids.length === 1) await setJournalPosted(ids[0], posted);
      else await setJournalsPosted(ids, posted);
      const listStore = useJournalStoreBase.getState();
      ids.forEach(id => listStore.updateItem(id, { isPosted: posted }));
      toast.success(t(posted ? 'journals.list.postSuccess' : 'journals.list.unpostSuccess', { count: ids.length }));
      bulkActions.setSelectedItems(bulkActions.selectedItems.filter(id => !ids.includes(id)));
      notifyDataChanged('journals');
    } catch (error) {
      toast.error(apiErrorMessage(error, posted ? 'common.messages.postFailed' : 'common.messages.unpostFailed'));
    } finally {
      setIsPosting(false);
      setPostingId(null);
      setPendingPost(null);
    }
  };

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('journals');
  };

  const addButton = noAccounts ? (
    <button type="button" disabled className={btnDisabled} title={t('journals.list.noAccountsError')}>
      {t('journals.list.addButton')}
    </button>
  ) : (
    <button type="button" onClick={() => openDialog(null)} className={btnPrimary}>
      {t('journals.list.addButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('journals.list.title')}</h1>
          <div className="flex items-center space-x-3 whitespace-nowrap">
            {admin && draftIds.length > 0 && (
              <button type="button" onClick={() => setPendingPost({ ids: draftIds, posted: true })} className={btnSecondary}>
                {t('journals.list.postButton', { count: draftIds.length })}
              </button>
            )}
            {admin && postedIds.length > 0 && (
              <button type="button" onClick={() => setPendingPost({ ids: postedIds, posted: false })} className={btnSecondary}>
                {t('journals.list.unpostButton', { count: postedIds.length })}
              </button>
            )}
            {admin && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                deleteDisabled={postedIds.length > 0}
                deleteDisabledMessage={t('journals.list.cannotDeletePosted')}
              />
            )}
            {addButton}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-3">
        <div className="md:w-80">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('journals.list.searchPlaceholder')} />
        </div>
        <div className="md:w-48">
          <CustomSelect
            value={statusFilter}
            onChange={value => setStatusFilter(value as StatusFilter)}
            options={STATUS_FILTERS.map(value => ({
              value,
              label: value === 'ALL' ? t('journals.list.allStatus') : t(value === 'POSTED' ? 'journals.status.posted' : 'journals.status.unposted'),
            }))}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setStatusFilter('ALL'); }}
            className="h-10 text-sm font-medium text-fg-secondary hover:text-fg self-start md:self-auto"
          >
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && journals.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead numeric className="w-28">{t('journals.table.date')}</TableHead>
                  <TableHead className="w-36">{t('journals.table.reference')}</TableHead>
                  <TableHead>{t('journals.table.description')}</TableHead>
                  <TableHead numeric className="w-36">{t('common.table.columns.amount')}</TableHead>
                  <TableHead className="w-24">{t('journals.table.status')}</TableHead>
                  <TableHead className="w-44">{t('journals.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS + 1}
                    filtered={hasFilters}
                    title={t('journals.list.noJournals')}
                    hint={noAccounts ? t('journals.list.noAccountsError') : t('journals.list.emptyHint')}
                    action={!noAccounts && addButton}
                  />
                ) : (
                  listItems.map(journal => {
                    const editable = canModify(journal) && !journal.source;
                    const busy = postingId === journal.id;
                    return (
                      <TableRow key={journal.id} className={pendingRowClass(journal)}>
                        <BulkSelectCell
                          userRole={userRole}
                          isSelected={bulkActions.selectedItems.includes(journal.id)}
                          onToggle={() => bulkActions.toggleItemSelection(journal.id)}
                        />
                        <TableCell numeric>{formatTableDate(journal.date, i18n.language)}</TableCell>
                        <TableCell className="truncate tabular-nums" title={journal.reference || undefined}>
                          {journal.reference || <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell className="truncate" title={journal.description}>
                          <span className="font-medium text-fg">{journal.description}</span>
                          {journal.source && <span className="block text-[13px] text-fg-secondary">{t('journals.list.fromInvoice')}</span>}
                        </TableCell>
                        <TableCell numeric><Amount value={Number(journal.totalAmount)} /></TableCell>
                        <TableCell>
                          <StatusDot
                            tone={journal.isPosted ? 'success' : 'neutral'}
                            label={journal.isPosted ? t('journals.status.posted') : t('journals.status.unposted')}
                          />
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            {journal.isPosted ? (
                              <>
                                <RowAction onClick={() => openDialog(journal)}>{t('common.actions.view')}</RowAction>
                                {editable && (
                                  <RowAction disabled={isPosting} onClick={() => applyPosted([journal.id], false)}>
                                    {busy ? t('common.status.unposting') : t('journals.actions.unpost')}
                                  </RowAction>
                                )}
                              </>
                            ) : (
                              <>
                                {editable && <RowAction onClick={() => openDialog(journal)}>{t('common.actions.edit')}</RowAction>}
                                {editable && (
                                  <RowAction disabled={isPosting} onClick={() => applyPosted([journal.id], true)}>
                                    {busy ? t('common.status.posting') : t('journals.actions.post')}
                                  </RowAction>
                                )}
                                {editable && (
                                  <RowAction tone="danger" onClick={() => store.handleDelete(journal.id)}>{t('common.actions.delete')}</RowAction>
                                )}
                                {!editable && <RowAction onClick={() => openDialog(journal, true)}>{t('common.actions.view')}</RowAction>}
                              </>
                            )}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('journals.list.confirmDeleteTitle')}
        message={t('journals.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('journals.list.confirmBulkDeleteTitle', { count: bulkActions.selectedItems.length })}
        message={t('journals.list.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={!!pendingPost}
        onClose={() => setPendingPost(null)}
        onConfirm={() => pendingPost && applyPosted(pendingPost.ids, pendingPost.posted)}
        title={pendingPost?.posted ? t('journals.list.confirmPostTitle', { count: pendingPost.ids.length }) : t('journals.list.confirmUnpostTitle', { count: pendingPost?.ids.length ?? 0 })}
        message={pendingPost?.posted ? t('journals.list.confirmPostMessage') : t('journals.list.confirmUnpostMessage')}
        confirmText={pendingPost?.posted ? t('journals.actions.post') : t('journals.actions.unpost')}
        cancelText={t('common.actions.cancel')}
        isLoading={isPosting}
        loadingText={pendingPost?.posted ? t('common.status.posting') : t('common.status.unposting')}
        t={t}
      />

      {dialogJournal !== undefined && <JournalDialog journal={dialogJournal ?? undefined} readOnly={viewOnly} onClose={() => setDialogJournal(undefined)} />}
    </div>
  );
};

export default JournalListPage;
